import { createRouter, createWebHistory } from "vue-router";
import AccountList from "../components/accounts/AccountList.vue";
import AccountForm from "../components/accounts/AccountForm.vue";
import DayReportOverview from "../components/day_reports/DayReportOverview.vue";
import DayReportSelected from "../components/day_reports/DayReportSelected.vue";
import DayReportForm from "../components/day_reports/DayReportForm.vue";
import DayReportList from "../components/day_reports/DayReportList.vue";
import DayReportTypeList from "../components/day_report_types/DayReportTypeList.vue";
import AccountDetail from "../components/accounts/AccountDetail.vue";
import DeadlineOverview from "../components/deadlines/DeadlineOverview.vue";
import ArticleOverview from "../components/articles/ArticleOverview.vue";
import LoginForm from "../components/auth/LoginForm.vue";
import LoginLegacyForm from "../components/auth/LoginLegacyForm.vue";
import ReportDetail from "../components/reports/ReportDetail.vue";
import ContactList from "../components/contacts/ContactList.vue";
import ContactDetail from "../components/contacts/ContactDetail.vue";
import ContactForm from "../components/contacts/ContactForm.vue";
import Planner from "../components/planner/Planner.vue";
import UserListRefactor from "../components/users/UserListRefactor.vue";
import UserDetail from "../components/users/UserDetail.vue";
import EnhancedExports from "../components/exports/EnhancedExports.vue";
import Empty from "../components/ui/Empty.vue";
import AccountCategoriesSettings from "../components/settings/AccountCategoriesSettings.vue";
import AccountLeadsSettings from "../components/settings/AccountLeadsSettings.vue";
import ReportCategoriesSettings from "../components/settings/ReportCategoriesSettings.vue";
import TraitsSettings from "../components/settings/TraitsSettings.vue";
import TraitCategoriesSettings from "../components/settings/TraitCategoriesSettings.vue";
import TenantsSettings from "../components/settings/TenantsSettings.vue";
import DeadlineCategoriesSettings from "../components/settings/DeadlineCategoriesSettings.vue";
import ContactCategoriesSettings from "../components/settings/ContactCategoriesSettings.vue";
import { useAuthStoreRefactor } from "../stores/authStoreRefactor";
import Geocode from "../components/geocode/Geocode.vue";

const TenantDetailSettings = () =>
  import("../components/settings/TenantDetailSettings.vue");
const AiPromptTemplatesSettings = () =>
  import("../components/settings/AiPromptTemplatesSettings.vue");
const AiUsageSettings = () =>
  import("../components/settings/AiUsageSettings.vue");
const AiModelsSettings = () =>
  import("../components/settings/AiModelsSettings.vue");
const AiRequestsSettings = () =>
  import("../components/settings/AiRequestsSettings.vue");
const AiRequestDetail = () =>
  import("../components/settings/AiRequestDetail.vue");

const routes = [
  { path: "/", component: DayReportOverview },
  { path: "/accounts", component: AccountList },
  { path: "/planner", component: Planner },
  { path: "/accounts/new", component: AccountForm },
  { path: "/accounts/:accountId", component: AccountDetail },
  { path: "/accounts/:accountId/edit", component: AccountForm },
  { path: "/accounts/:accountId/reports/:id", component: ReportDetail },
  { path: "/day_reports", component: DayReportList },
  { path: "/day_report_types", component: DayReportTypeList },
  { path: "/settings/day-report-types", component: DayReportTypeList },
  {
    path: "/settings/account-categories",
    component: AccountCategoriesSettings,
  },
  {
    path: "/settings/account-leads",
    component: AccountLeadsSettings,
  },
  {
    path: "/settings/report-categories",
    component: ReportCategoriesSettings,
  },
  {
    path: "/settings/traits",
    component: TraitsSettings,
  },
  {
    path: "/settings/trait-categories",
    component: TraitCategoriesSettings,
  },
  {
    path: "/settings/tenants",
    component: TenantsSettings,
  },
  {
    path: "/settings/deadline-categories",
    component: DeadlineCategoriesSettings,
  },
  {
    path: "/settings/contact-categories",
    component: ContactCategoriesSettings,
  },
  {
    path: "/settings/tenants/:id",
    component: TenantDetailSettings,
    meta: { requiresAdmin: true },
  },
  {
    path: "/settings/ai",
    component: AiPromptTemplatesSettings,
    meta: { requiresAdmin: true },
  },
  {
    path: "/settings/ai-usage",
    component: AiUsageSettings,
    meta: { requiresAdmin: true },
  },
  {
    path: "/settings/ai-models",
    component: AiModelsSettings,
    meta: { requiresAdmin: true },
  },
  {
    path: "/settings/ai-requests",
    component: AiRequestsSettings,
    meta: { requiresAdmin: true },
  },
  {
    path: "/settings/ai-requests/:id",
    component: AiRequestDetail,
    meta: { requiresAdmin: true },
  },
  // Old address of the instructions page
  { path: "/settings/ai-templates", redirect: "/settings/ai" },
  { path: "/day_reports/:id", component: DayReportSelected },
  { path: "/day_reports/new", component: DayReportForm },
  { path: "/reports/:id", component: ReportDetail },
  { path: "/login", component: LoginForm },
  { path: "/login_backup", component: LoginLegacyForm },
  { path: "/test", component: Empty },
  { path: "/deadlines", component: DeadlineOverview },
  { path: "/contacts", component: ContactList },
  { path: "/contacts/:id/edit", component: ContactForm },
  { path: "/contacts/new", component: ContactForm },
  { path: "/contacts/:id", component: ContactDetail },
  { path: "/users", component: UserListRefactor },
  { path: "/users/:id", component: UserDetail },
  { path: "/exports", component: EnhancedExports },
  { path: "/articles", component: ArticleOverview },
  { path: "/geocode", component: Geocode },
];

export const router = new createRouter({
  history: createWebHistory(),
  routes: routes,
  scrollBehavior(to, from, savedPosition) {
    // always scroll to top
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.beforeEach((to, from, next) => {
  // redirect to login page if not logged in and trying to access a restricted page
  const publicPages = ["/login", "/login_backup"];
  const authRequired = !publicPages.includes(to.path);
  const loggedIn = localStorage.getItem("token");

  if (authRequired && !loggedIn) {
    // Clear auth store to hide UI elements immediately
    const authStore = useAuthStoreRefactor();
    if (authStore.loggedIn) {
      console.log(
        "Router: Token missing but authStore still logged in - clearing auth state",
      );
      authStore.clearAuth();
    }
    return next("/login");
  }

  // Check if route requires a role (super_admin for global settings, else admin)
  const requiredRole = to.meta.requiresSuperAdmin
    ? "super_admin"
    : to.meta.requiresAdmin
      ? "admin"
      : null;
  if (requiredRole) {
    const authStore = useAuthStoreRefactor();

    // If user data not loaded yet, load it first
    if (!authStore.user) {
      authStore
        .getCurrentUser()
        .then((user) => {
          if (user && user.roles && user.roles.includes(requiredRole)) {
            next();
          } else {
            // Redirect to home if the role is missing
            next("/");
          }
        })
        .catch(() => {
          // If there's an error fetching user data, redirect to home
          next("/");
        });
    } else {
      // User data already loaded, check for the role
      if (authStore.user.roles && authStore.user.roles.includes(requiredRole)) {
        next();
      } else {
        // Redirect to home if the role is missing
        next("/");
      }
    }
  } else {
    next();
  }
});
